// Document detail — redacted text view, tag editor, "View original"
// Vault download, per-document Activity tab.

import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { ApiError, documents } from "../lib/api";
import { useAsync } from "../hooks/useAsync";
import { pushToast } from "../components/Toast";
import { CrossCheckableBadge, DocumentStatusBadge } from "../components/StatusBadge";

export function DocumentDetailPage(): JSX.Element {
  const params = useParams<{ documentId: string }>();
  const documentId = Number(params.documentId);
  const { data, loading, error, refresh } = useAsync(
    () => documents.text(documentId),
    [documentId],
  );
  const { data: activity } = useAsync(
    () => documents.activity(documentId),
    [documentId],
  );
  const [tagDraft, setTagDraft] = useState("");
  const [savingTags, setSavingTags] = useState(false);
  const [downloading, setDownloading] = useState(false);

  if (Number.isNaN(documentId)) return <p className="text-sm text-red-700">Invalid document id.</p>;
  if (loading) return <p className="text-sm text-slate-500">Loading document…</p>;
  if (error) return <p className="text-sm text-red-700">{error.message}</p>;
  if (!data) return <p className="text-sm text-slate-500">No document.</p>;

  const tags = data.tags;

  const addTag = async (): Promise<void> => {
    const t = tagDraft.trim().toLowerCase();
    if (!t) return;
    if (tags.includes(t)) {
      setTagDraft("");
      return;
    }
    setSavingTags(true);
    try {
      await documents.updateTags(documentId, [...tags, t]);
      setTagDraft("");
      refresh();
    } catch (err) {
      pushToast("error", err instanceof ApiError ? err.message : "Failed to save tag");
    } finally {
      setSavingTags(false);
    }
  };

  const removeTag = async (t: string): Promise<void> => {
    setSavingTags(true);
    try {
      await documents.updateTags(
        documentId,
        tags.filter((x) => x !== t),
      );
      refresh();
    } catch (err) {
      pushToast("error", err instanceof ApiError ? err.message : "Failed to remove tag");
    } finally {
      setSavingTags(false);
    }
  };

  const onDownloadOriginal = async (): Promise<void> => {
    setDownloading(true);
    try {
      const blob = await documents.original(documentId);
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement("a");
      a.href = url;
      a.download = data.filename;
      window.document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      pushToast(
        "success",
        `Original retrieved from Vault (audit row recorded)`,
      );
    } catch (err) {
      pushToast("error", err instanceof ApiError ? err.message : "Vault retrieval failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div>
      <nav className="mb-2 text-sm text-slate-500">
        <Link to="/cases" className="hover:text-brand-700">
          Cases
        </Link>
        <span aria-hidden> / </span>
        <span>Document</span>
      </nav>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{data.filename}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500">
            <DocumentStatusBadge status={data.status} />
            <CrossCheckableBadge cross_checkable={data.cross_checkable} />
            <span>{data.doc_type ?? "uncategorized"}</span>
            <span>·</span>
            <span>{Math.round(data.byte_size / 1024)} KB</span>
            <span>·</span>
            <span>{new Date(data.created_at).toLocaleString()}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn-primary"
            disabled={downloading || data.status !== "complete"}
            onClick={onDownloadOriginal}
          >
            {downloading ? "Retrieving…" : "View original (Vault)"}
          </button>
        </div>
      </div>

      <section className="card mb-6 p-5">
        <h2 className="mb-3 text-lg font-medium text-slate-800">Tags</h2>
        <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
          {tags.length === 0 && <span className="text-slate-500">No tags yet.</span>}
          {tags.map((t) => (
            <span key={t} className="badge-blue inline-flex items-center gap-1">
              #{t}
              <button
                type="button"
                aria-label={`Remove tag ${t}`}
                className="text-brand-700 hover:text-brand-900"
                onClick={() => removeTag(t)}
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void addTag();
          }}
          className="flex items-center gap-2"
        >
          <input
            aria-label="New tag"
            placeholder="Add a tag (e.g. denial, medical-record)"
            className="input max-w-xs"
            value={tagDraft}
            onChange={(e) => setTagDraft(e.target.value)}
            disabled={savingTags}
          />
          <button type="submit" className="btn-secondary" disabled={savingTags || !tagDraft.trim()}>
            Add
          </button>
        </form>
      </section>

      <section className="card mb-6 p-5">
        <h2 className="mb-3 text-lg font-medium text-slate-800">Redacted text</h2>
        <p className="mb-2 text-xs text-slate-500">
          PII is replaced with typed placeholders (<code>[NAME-1]</code>, <code>[DOB-1]</code>,
          etc.) during ingestion. This is the version used for search, RAG, and the cross-check
          engine — the unredacted original is only readable via the audited Vault retrieval
          above.
        </p>
        {data.text ? (
          <pre className="max-h-96 overflow-y-auto whitespace-pre-wrap rounded-md bg-slate-50 p-4 text-xs leading-relaxed text-slate-800">
            {data.text}
          </pre>
        ) : (
          <p className="text-sm text-slate-500">
            {data.status === "complete"
              ? "No text available."
              : `Ingestion still in progress (status: ${data.status}).`}
          </p>
        )}
      </section>

      <section className="card p-5">
        <h2 className="mb-3 text-lg font-medium text-slate-800">Activity</h2>
        <p className="mb-3 text-xs text-slate-500">
          Per SPEC §3.10, every Vault read appends an entry below. This is the only audit
          surface in MVP (account-wide History view is deferred per §7 Q13).
        </p>
        {!activity && <p className="text-sm text-slate-500">Loading…</p>}
        {activity && activity.entries.length === 0 && (
          <p className="text-sm text-slate-500">No events yet.</p>
        )}
        {activity && activity.entries.length > 0 && (
          <ol role="list" className="space-y-2">
            {activity.entries.map((e) => (
              <li key={e.id} className="border-l-2 border-brand-300 pl-3 text-sm">
                <div className="font-medium text-slate-800">{e.action}</div>
                <div className="text-xs text-slate-500">
                  {new Date(e.timestamp).toLocaleString()} · user #{e.user_id}
                  {e.request_ip ? ` · ${e.request_ip}` : ""}
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}
